/**
 * User Table Component
 * Displays users in a table with actions
 */

import React from "react";
import Table from "../../../components/table/table";
import Icon from "../../../components/icon/icon";
import { TableColumn } from "../../../types";
import { UserEntity, UserTableProps } from "../types/user.types";
import { formatDate } from "../../../utils/format";
import { Badge } from "../../../components/ui/badge";
import { ITEMS_PER_PAGE_OPTIONS } from "../../../constants/pagination";

/**
 * User table component with pagination and actions
 */
export const UserTable: React.FC<UserTableProps> = ({
  users,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  currentPage,
  itemsPerPage,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
}) => {
  // Define columns for the user table
  const columns: TableColumn<UserEntity>[] = [
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      sortable: true,
      render: (value: string, record: UserEntity) => (
        <div>
          <div className="font-medium">{value || "—"}</div>
        </div>
      ),
    },
    {
      key: "role",
      title: "Роль",
      dataIndex: "role",
      sortable: true,
      render: (value: string) => {
        const roleLabels: Record<string, string> = {
          REGULAR: "Обычный пользователь",
          ADMIN: "Администратор",
          MULTI_BRANCH_ADMIN: "Администратор всех филиалов",
    SUPER_ADMIN: "Супер администратор",
          DEVELOPER: "Разработчик",
        };
        const roleColors: Record<
          string,
          "default" | "success" | "warning" | "error"
        > = {
          REGULAR: "default",
          ADMIN: "warning",
          MULTI_BRANCH_ADMIN: "warning",
          SUPER_ADMIN: "error",
          DEVELOPER: "success",
        };
        return (
          <Badge variant={roleColors[value] || "default"}>
            {roleLabels[value] || value}
          </Badge>
        );
      },
    },
    {
      key: "branch",
      title: "Филиал",
      dataIndex: "branch",
      render: (value: any) => value?.name || "—",
    },
    {
      key: "createdAt",
      title: "Дата создания",
      dataIndex: "createdAt",
      sortable: true,
      render: (value: string) => formatDate(value),
    },
    {
      key: "actions",
      title: "Действия",
      align: "right",
      render: (_: any, record: UserEntity) => (
        <div className="table-actions">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(record);
            }}
            className="table-action-btn"
            disabled={isLoading}
            title="Редактировать"
          >
            <Icon name="pencil" size="small" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(record);
            }}
            className="table-action-btn"
            disabled={isLoading}
            title="Удалить"
          >
            <Icon name="delete" size="small" />
          </button>
        </div>
      ),
    },
  ];

  const handleItemsPerPageChange = (value: string): void => {
    onItemsPerPageChange(parseInt(value));
  };

  return (
    <div className="users__table-container">
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={isLoading}
        emptyMessage="Пользователи не найдены"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />

      <div className="users__pagination">
        <div className="users__pagination-info">
          Показано{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, users.length)} -{" "}
          {Math.min(currentPage * itemsPerPage, users.length)} из {users.length}
        </div>

        <div className="users__pagination-controls">
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
            className="users__pagination-select"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} на странице
              </option>
            ))}
          </select>

          <div className="users__page-buttons">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="users__page-btn"
            >
              <Icon name="left" size="small" />
            </button>

            <span className="users__page-info">
              {currentPage} из {totalPages}
            </span>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="users__page-btn"
            >
              <Icon name="right" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
